// pages/Installs.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Search,
  RefreshCw,
  Upload,
  Plus,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  X,
  Check,
  AlertCircle,
  Filter,
  Calendar,
  FileText,
  TrendingUp,
  Clock,
  Link2,
  ExternalLink,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

// ─── constants ────────────────────────────────────────────────────────────────

const STATUSES = [
  "Install Booked",
  "Install Booked Monthly",
  "Install Completed",
  "Install Completed Monthly",
  "Finance Check",
  "Sales Issues",
  "Confirm Install Date",
  "Paperwork Missing",
  "Waiting BB",
  "Survey Required",
  "Install Incomplete",
  "DEAD OFF",
];

const STATUS_COLORS = {
  "Install Booked": "bg-blue-500/10 text-blue-400 border-blue-500/30",
  "Install Booked Monthly": "bg-blue-500/10 text-blue-400 border-blue-500/30",
  "Install Completed": "bg-green-500/10 text-green-400 border-green-500/30",
  "Install Completed Monthly":
    "bg-green-500/10 text-green-400 border-green-500/30",
  "Finance Check": "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  "Sales Issues": "bg-orange-500/10 text-orange-400 border-orange-500/30",
  "Confirm Install Date":
    "bg-purple-500/10 text-purple-400 border-purple-500/30",
  "Paperwork Missing": "bg-red-500/10 text-red-400 border-red-500/30",
  "Waiting BB": "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  "Survey Required": "bg-indigo-500/10 text-indigo-400 border-indigo-500/30",
  "Install Incomplete": "bg-amber-500/10 text-amber-400 border-amber-500/30",
  "DEAD OFF": "bg-gray-500/10 text-gray-400 border-gray-500/30",
};

const STATUS_COLORS_LIGHT = {
  "Install Booked": "bg-blue-50 text-blue-700 border-blue-200",
  "Install Booked Monthly": "bg-blue-50 text-blue-700 border-blue-200",
  "Install Completed": "bg-green-50 text-green-700 border-green-200",
  "Install Completed Monthly": "bg-green-50 text-green-700 border-green-200",
  "Finance Check": "bg-yellow-50 text-yellow-700 border-yellow-200",
  "Sales Issues": "bg-orange-50 text-orange-700 border-orange-200",
  "Confirm Install Date": "bg-purple-50 text-purple-700 border-purple-200",
  "Paperwork Missing": "bg-red-50 text-red-700 border-red-200",
  "Waiting BB": "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Survey Required": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Install Incomplete": "bg-amber-50 text-amber-700 border-amber-200",
  "DEAD OFF": "bg-gray-100 text-gray-500 border-gray-200",
};

const EMPTY_FORM = {
  accountName: "",
  accountId: "",
  opportunityId: "",
  provInstallDate: "",
  installDate: "",
  status: "Install Booked",
  projectOwner: "",
  salesExec: "",
  funder: "",
  dateSold: "",
  dealValue: "",
  notes: "",
};

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(date) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtCurrency(val) {
  if (val === null || val === undefined) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
  }).format(val);
}

// ─── sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, isDarkMode }) {
  return (
    <div
      className={`rounded-xl p-5 border transition-colors ${
        isDarkMode
          ? "bg-[#0C0E12] border-gray-700/50"
          : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className={`text-[13px] font-medium ${isDarkMode ? "text-[#94979C]" : "text-gray-500"}`}
        >
          {label}
        </span>
        <Icon
          size={16}
          className={isDarkMode ? "text-gray-500" : "text-gray-400"}
        />
      </div>
      <div
        className={`text-2xl font-bold ${isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"}`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status, isDarkMode }) {
  const cls = isDarkMode ? STATUS_COLORS[status] : STATUS_COLORS_LIGHT[status];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-medium ${cls}`}
    >
      {status}
    </span>
  );
}

// ─── install modal ────────────────────────────────────────────────────────────

function InstallModal({ isDarkMode, install, onClose, onSave }) {
  const [form, setForm] = useState(
    install
      ? {
          accountName: install.accountName || "",
          accountId: install.accountId || "",
          opportunityId: install.opportunityId || "",
          provInstallDate: install.provInstallDate || "",
          installDate: install.installDate || "",
          status: install.status || "Install Booked",
          projectOwner: install.projectOwner || "",
          salesExec: install.salesExec || "",
          funder: install.funder || "",
          dateSold: install.dateSold || "",
          dealValue:
            install.dealValue !== null && install.dealValue !== undefined
              ? String(install.dealValue)
              : "",
          notes: install.notes || "",
        }
      : { ...EMPTY_FORM },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Account search
  const [accountSearch, setAccountSearch] = useState(
    install?.account?.name || install?.accountName || "",
  );
  const [accountResults, setAccountResults] = useState([]);
  const [searchingAccounts, setSearchingAccounts] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const accountSearchRef = useRef(null);
  const searchTimeout = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (
        accountSearchRef.current &&
        !accountSearchRef.current.contains(e.target)
      ) {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleAccountSearch = (value) => {
    setAccountSearch(value);

    // Clearing the field also clears the linked accountId
    if (!value.trim()) {
      setForm((p) => ({ ...p, accountId: "", accountName: "" }));
      setAccountResults([]);
      setShowAccountDropdown(false);
      return;
    }

    // Keep accountName in sync as free text until a real account is picked
    setForm((p) => ({ ...p, accountName: value, accountId: "" }));

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearchingAccounts(true);
      try {
        const res = await apiService.getAccounts({ search: value, limit: 8 });
        setAccountResults(res?.data || []);
        setShowAccountDropdown(true);
      } catch {
        setAccountResults([]);
      } finally {
        setSearchingAccounts(false);
      }
    }, 300);
  };

  const selectAccount = (account) => {
    setForm((p) => ({
      ...p,
      accountId: account.id,
      accountName: account.name,
    }));
    setAccountSearch(account.name);
    setShowAccountDropdown(false);
    setAccountResults([]);
  };

  const clearAccountLink = () => {
    // Removes the FK link but keeps the display name as free text
    setForm((p) => ({ ...p, accountId: "" }));
  };

  const field = (label, key, type = "text", opts = {}) => (
    <div>
      <label
        className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
      >
        {label}
      </label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
        className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-600"
            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
        }`}
        {...opts}
      />
    </div>
  );

  const handleSubmit = async () => {
    if (!form.accountName.trim()) {
      setError("Account name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        dealValue: form.dealValue !== "" ? parseFloat(form.dealValue) : null,
        accountId: form.accountId || null,
        opportunityId: form.opportunityId || null,
        provInstallDate: form.provInstallDate || null,
        installDate: form.installDate || null,
        dateSold: form.dateSold || null,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const overlay = "fixed inset-0 z-50 flex items-center justify-center p-4";
  const backdrop = "absolute inset-0 bg-black/60 backdrop-blur-sm";
  const panel = `relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
    isDarkMode
      ? "bg-[#0A0B0F] border border-gray-700/50"
      : "bg-white border border-gray-200"
  }`;

  return (
    <div className={overlay}>
      <div className={backdrop} onClick={onClose} />
      <div className={panel}>
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700/50" : "border-gray-200"}`}
        >
          <h2
            className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {install ? "Edit Install" : "New Install"}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-700/40 transition-colors"
          >
            <X
              size={18}
              className={isDarkMode ? "text-gray-400" : "text-gray-500"}
            />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {/* Account search / link */}
          <div ref={accountSearchRef} className="relative">
            <label
              className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              Account / Client Name *
            </label>
            <div className="relative">
              <input
                type="text"
                value={accountSearch}
                onChange={(e) => handleAccountSearch(e.target.value)}
                placeholder="Type to search accounts, or enter a name…"
                className={`w-full rounded-lg border px-3 py-2 pr-8 text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-600"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                } ${
                  form.accountId
                    ? isDarkMode
                      ? "border-blue-500/60"
                      : "border-blue-400"
                    : ""
                }`}
              />
              {searchingAccounts && (
                <RefreshCw
                  size={13}
                  className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400 pointer-events-none"
                />
              )}
            </div>

            {/* Linked badge */}
            {form.accountId && (
              <div
                className={`mt-1.5 flex items-center gap-1.5 text-xs ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
              >
                <Link2 size={11} />
                <span>Linked to account record</span>
                <button
                  onClick={clearAccountLink}
                  title="Unlink (keeps name as free text)"
                  className="ml-auto text-gray-400 hover:text-red-400 transition-colors"
                >
                  <X size={11} />
                </button>
              </div>
            )}

            {/* Dropdown */}
            {showAccountDropdown && accountResults.length > 0 && (
              <div
                className={`absolute z-20 left-0 right-0 mt-1 rounded-lg border shadow-xl overflow-hidden ${
                  isDarkMode
                    ? "bg-[#0A0B0F] border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                {accountResults.map((acc) => (
                  <button
                    key={acc.id}
                    onMouseDown={() => selectAccount(acc)}
                    className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-3 transition-colors ${
                      isDarkMode
                        ? "hover:bg-gray-800 text-white"
                        : "hover:bg-gray-50 text-gray-900"
                    }`}
                  >
                    <span className="font-medium truncate">{acc.name}</span>
                    {acc.type && (
                      <span
                        className={`text-xs shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        {acc.type}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dates row */}
          <div className="grid grid-cols-2 gap-4">
            {field("Prov. Install Date", "provInstallDate", "date")}
            {field("Install Date", "installDate", "date")}
          </div>

          {/* Status */}
          <div>
            <label
              className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              Status
            </label>
            <select
              value={form.status}
              onChange={(e) =>
                setForm((p) => ({ ...p, status: e.target.value }))
              }
              className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* People row */}
          <div className="grid grid-cols-2 gap-4">
            {field("Project Owner", "projectOwner")}
            {field("Sales Exec", "salesExec")}
          </div>

          {/* Finance row */}
          <div className="grid grid-cols-3 gap-4">
            {field("Funder", "funder")}
            {field("Date Sold", "dateSold", "date")}
            {field("Deal Value (£)", "dealValue", "number", {
              step: "0.01",
              min: "0",
            })}
          </div>

          {/* Notes */}
          <div>
            <label
              className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              Notes
            </label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) =>
                setForm((p) => ({ ...p, notes: e.target.value }))
              }
              className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors resize-none ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-600"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
              }`}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-end gap-3 p-6 border-t ${isDarkMode ? "border-gray-700/50" : "border-gray-200"}`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              isDarkMode
                ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {saving ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <Check size={14} />
            )}
            {saving ? "Saving…" : "Save Install"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── import modal ─────────────────────────────────────────────────────────────

function ImportModal({ isDarkMode, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    try {
      const res = await apiService.importInstallsXlsx(file);
      setResult(res);
      onDone();
    } catch (err) {
      setError(err?.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const overlay = "fixed inset-0 z-50 flex items-center justify-center p-4";
  const backdrop = "absolute inset-0 bg-black/60 backdrop-blur-sm";
  const panel = `relative w-full max-w-lg rounded-2xl shadow-2xl ${
    isDarkMode
      ? "bg-[#0A0B0F] border border-gray-700/50"
      : "bg-white border border-gray-200"
  }`;

  return (
    <div className={overlay}>
      <div className={backdrop} onClick={onClose} />
      <div className={panel}>
        <div
          className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700/50" : "border-gray-200"}`}
        >
          <h2
            className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Import from Excel
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-700/40 transition-colors"
          >
            <X
              size={18}
              className={isDarkMode ? "text-gray-400" : "text-gray-500"}
            />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!result ? (
            <>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Upload your INSTALLS spreadsheet (.xlsx). The importer reads the
                first sheet and maps columns automatically based on the expected
                headers.
              </p>

              <div
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  isDarkMode
                    ? "border-gray-700 hover:border-blue-500/50 text-gray-500"
                    : "border-gray-300 hover:border-blue-400 text-gray-400"
                }`}
              >
                <Upload size={28} className="mx-auto mb-2" />
                <p className="text-sm font-medium">
                  {file ? file.name : "Click to select .xlsx file"}
                </p>
                {file && (
                  <p
                    className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {(file.size / 1024).toFixed(0)} KB
                  </p>
                )}
              </div>

              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => setFile(e.target.files[0] || null)}
              />

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  <AlertCircle size={14} />
                  {error}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm">
                <Check size={14} />
                Import complete
              </div>
              <div
                className={`rounded-lg p-4 border ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
              >
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div
                      className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {result.summary?.created ?? 0}
                    </div>
                    <div
                      className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Created
                    </div>
                  </div>
                  <div>
                    <div
                      className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {result.summary?.skipped ?? 0}
                    </div>
                    <div
                      className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Skipped
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-400">
                      {result.summary?.errors ?? 0}
                    </div>
                    <div
                      className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Errors
                    </div>
                  </div>
                </div>
              </div>
              {result.errorDetails?.length > 0 && (
                <div
                  className={`rounded-lg p-3 border text-xs max-h-32 overflow-y-auto ${isDarkMode ? "bg-red-950/20 border-red-800/30 text-red-400" : "bg-red-50 border-red-200 text-red-700"}`}
                >
                  {result.errorDetails.map((e, i) => (
                    <div key={i}>
                      Row {e.row}: {e.name} — {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={`flex items-center justify-end gap-3 p-6 border-t ${isDarkMode ? "border-gray-700/50" : "border-gray-200"}`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              isDarkMode
                ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {result ? "Close" : "Cancel"}
          </button>
          {!result && (
            <button
              onClick={handleImport}
              disabled={!file || importing}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {importing ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Upload size={14} />
              )}
              {importing ? "Importing…" : "Import"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

const Installs = () => {
  const { isDarkMode } = useTheme();

  const [installs, setInstalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    projectOwner: "",
    salesExec: "",
    startDate: "",
    endDate: "",
  });
  const [sortBy, setSortBy] = useState("installDate");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editInstall, setEditInstall] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchInstalls = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: pagination.limit,
          sortBy,
          sortOrder,
          ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        };
        const res = await apiService.getInstalls(params);
        setInstalls(res.data || []);
        setPagination((p) => ({ ...p, ...res.pagination, page }));
      } catch (err) {
        console.error("Failed to fetch installs:", err);
      } finally {
        setLoading(false);
      }
    },
    [filters, sortBy, sortOrder, pagination.limit],
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiService.getInstallStats();
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchInstalls(1);
  }, [filters, sortBy, sortOrder]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(col);
      setSortOrder("ASC");
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronDown size={12} className="opacity-30" />;
    return sortOrder === "ASC" ? (
      <ChevronUp size={12} />
    ) : (
      <ChevronDown size={12} />
    );
  };

  const handleCreate = async (data) => {
    await apiService.createInstall(data);
    fetchInstalls(1);
    fetchStats();
  };

  const handleUpdate = async (data) => {
    await apiService.updateInstall(editInstall.id, data);
    fetchInstalls(pagination.page);
  };

  const handleDelete = async (id) => {
    await apiService.deleteInstall(id);
    setDeleteConfirm(null);
    fetchInstalls(pagination.page);
    fetchStats();
  };

  // ── render ──
  const th = `py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none ${
    isDarkMode ? "text-gray-500" : "text-gray-500"
  }`;

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1
              className={`text-2xl sm:text-3xl font-bold mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Installs
            </h1>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Track and manage all installation jobs
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => fetchInstalls(pagination.page)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-800"
                  : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={() => setShowImport(true)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-800"
                  : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Upload size={14} />
              Import XLSX
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus size={14} />
              New Install
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Total Installs"
              value={stats.total}
              icon={FileText}
              isDarkMode={isDarkMode}
            />
            <StatCard
              label="This Month"
              value={stats.monthlyInstalls}
              icon={Calendar}
              isDarkMode={isDarkMode}
            />
            <StatCard
              label="Total Deal Value"
              value={fmtCurrency(stats.totalValue)}
              icon={TrendingUp}
              isDarkMode={isDarkMode}
            />
            <StatCard
              label="Booked"
              value={
                (stats.statusCounts?.["Install Booked"] || 0) +
                (stats.statusCounts?.["Install Booked Monthly"] || 0)
              }
              icon={Clock}
              isDarkMode={isDarkMode}
            />
          </div>
        )}

        {/* Search + filter bar */}
        <div
          className={`rounded-xl border p-4 mb-4 ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200"}`}
        >
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search
                size={14}
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              />
              <input
                type="text"
                placeholder="Search installs…"
                value={filters.search}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, search: e.target.value }))
                }
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
                  isDarkMode
                    ? "bg-[#0A0B0F] border-gray-700 text-white placeholder-gray-600"
                    : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                }`}
              />
            </div>

            {/* Status */}
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters((p) => ({ ...p, status: e.target.value }))
              }
              className={`px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
                isDarkMode
                  ? "bg-[#0A0B0F] border-gray-700 text-white"
                  : "bg-gray-50 border-gray-200 text-gray-900"
              }`}
            >
              <option value="">All Statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <button
              onClick={() => setShowFilters((p) => !p)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters
                  ? "bg-blue-600 border-blue-600 text-white"
                  : isDarkMode
                    ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Filter size={14} />
              Filters
            </button>
          </div>

          {/* Expanded filters */}
          {showFilters && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-700/30">
              {[
                ["Project Owner", "projectOwner"],
                ["Sales Exec", "salesExec"],
                ["From Date", "startDate", "date"],
                ["To Date", "endDate", "date"],
              ].map(([label, key, type = "text"]) => (
                <div key={key}>
                  <label
                    className={`block text-xs mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                  >
                    {label}
                  </label>
                  <input
                    type={type}
                    value={filters[key]}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, [key]: e.target.value }))
                    }
                    className={`w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 ${
                      isDarkMode
                        ? "bg-[#0A0B0F] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Table */}
        <div
          className={`rounded-xl border overflow-hidden ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200"}`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead
                className={`border-b ${isDarkMode ? "border-gray-700/50 bg-[#0A0B0F]" : "border-gray-200 bg-gray-50"}`}
              >
                <tr>
                  {[
                    { label: "Account / Client", col: "accountName" },
                    { label: "Install Date", col: "installDate" },
                    { label: "Prov. Date", col: "provInstallDate" },
                    { label: "Status", col: "status" },
                    { label: "Project Owner", col: "projectOwner" },
                    { label: "Sales Exec", col: "salesExec" },
                    { label: "Funder", col: "funder" },
                    { label: "Deal Value", col: "dealValue" },
                    { label: "Notes", col: null },
                    { label: "", col: null },
                  ].map(({ label, col }) => (
                    <th
                      key={label}
                      className={th}
                      onClick={col ? () => handleSort(col) : undefined}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {col && <SortIcon col={col} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr
                      key={i}
                      className={`border-b ${isDarkMode ? "border-gray-700/30" : "border-gray-100"}`}
                    >
                      {Array.from({ length: 10 }).map((__, j) => (
                        <td key={j} className="py-4 px-4">
                          <div
                            className={`h-4 rounded ${isDarkMode ? "bg-gray-800" : "bg-gray-200"} animate-pulse`}
                            style={{ width: `${60 + Math.random() * 40}%` }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : installs.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-16 text-center">
                      <FileText
                        size={32}
                        className={`mx-auto mb-3 ${isDarkMode ? "text-gray-700" : "text-gray-300"}`}
                      />
                      <p
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      >
                        No installs found
                      </p>
                    </td>
                  </tr>
                ) : (
                  installs.map((install) => (
                    <tr
                      key={install.id}
                      className={`border-b transition-colors ${
                        isDarkMode
                          ? "border-gray-700/30 hover:bg-gray-800/30"
                          : "border-gray-100 hover:bg-gray-50"
                      }`}
                    >
                      {/* Account / Client — with optional linked account */}
                      <td className="py-3 px-4">
                        <p
                          className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {install.accountName}
                        </p>
                        {install.account && (
                          <a
                            href={`/accounts/${install.account.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className={`inline-flex items-center gap-1 mt-0.5 text-xs hover:underline transition-colors ${
                              isDarkMode
                                ? "text-blue-400 hover:text-blue-300"
                                : "text-blue-600 hover:text-blue-700"
                            }`}
                          >
                            <ExternalLink size={10} />
                            {install.account.name}
                          </a>
                        )}
                      </td>

                      <td
                        className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {fmt(install.installDate)}
                      </td>
                      <td
                        className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {fmt(install.provInstallDate)}
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge
                          status={install.status}
                          isDarkMode={isDarkMode}
                        />
                      </td>
                      <td
                        className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {install.projectOwner || "—"}
                      </td>
                      <td
                        className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {install.salesExec || "—"}
                      </td>
                      <td
                        className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {install.funder || "—"}
                      </td>
                      <td
                        className={`py-3 px-4 text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                      >
                        {fmtCurrency(install.dealValue)}
                      </td>
                      <td
                        className={`py-3 px-4 text-xs max-w-[200px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        <p className="truncate" title={install.notes || ""}>
                          {install.notes || "—"}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditInstall(install)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isDarkMode
                                ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                                : "hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                            }`}
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(install)}
                            className="p-1.5 rounded-lg transition-colors text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div
              className={`flex items-center justify-between px-4 py-3 border-t ${isDarkMode ? "border-gray-700/50 bg-[#0A0B0F]" : "border-gray-200 bg-gray-50"}`}
            >
              <p
                className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchInstalls(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className={`px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 transition-colors ${
                    isDarkMode
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchInstalls(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className={`px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 transition-colors ${
                    isDarkMode
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <InstallModal
          isDarkMode={isDarkMode}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
        />
      )}
      {editInstall && (
        <InstallModal
          isDarkMode={isDarkMode}
          install={editInstall}
          onClose={() => setEditInstall(null)}
          onSave={handleUpdate}
        />
      )}
      {showImport && (
        <ImportModal
          isDarkMode={isDarkMode}
          onClose={() => setShowImport(false)}
          onDone={() => {
            fetchInstalls(1);
            fetchStats();
          }}
        />
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          />
          <div
            className={`relative w-full max-w-sm rounded-2xl p-6 shadow-2xl ${isDarkMode ? "bg-[#0A0B0F] border border-gray-700/50" : "bg-white border border-gray-200"}`}
          >
            <h3
              className={`text-base font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Delete Install
            </h3>
            <p
              className={`text-sm mb-5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              Are you sure you want to delete{" "}
              <strong>{deleteConfirm.accountName}</strong>? This cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  isDarkMode
                    ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Installs;
